<template>
    <div>
        <rkm-row>
            <rkm-col>
                <rkm-table
                    :busy="busy"
                    :fields="fields"
                    :filter="model.filter"
                    :hide-pagination="hidePagination"
                    :items="customFilter(filteredItems)"
                    :sort-by="model.sortBy"
                    :tbody-tr-attr="handleRowAttr"
                    ref="RkmTable"
                >
                    <template v-slot:filters>
                        <rkm-area
                            no-margin-top
                            title="Filtros"
                            type="fieldset"
                        >
                            <rkm-row>
                                <rkm-col cols="8">
                                    <rkm-form-input
                                        label="Buscar"
                                        placeholder="Digite para buscar"
                                        v-model="model.filter"
                                    />
                                </rkm-col>

                                <rkm-col cols="4">
                                    <rkm-form-select
                                        :options="form.sortOptions"
                                        @change="handleSortChange"
                                        label="Ordenar"
                                        v-model="model.sortBy"
                                    />
                                </rkm-col>
                            </rkm-row>

                            <rkm-row>
                                <rkm-col>
                                    <rkm-form-select
                                        :options="form.temposEspera"
                                        label="Tempo de espera"
                                        text-field="minutos"
                                        v-model="model.tempoEsperaId"
                                        value-field="id"
                                        show-default-option
                                        default-option-text="Todos"
                                    />
                                </rkm-col>

                                <rkm-col>
                                    <rkm-form-select
                                        :options="form.opcoesEntrada"
                                        label="Opção de entrada"
                                        text-field="nome"
                                        v-model="model.opcaoEntradaId"
                                        value-field="id"
                                        show-default-option
                                        default-option-text="Todas"
                                    />
                                </rkm-col>

                                <rkm-col>
                                    <rkm-form-select
                                        :options="form.prioridades"
                                        label="Prioridade"
                                        text-field="nome"
                                        v-model="model.prioridadeId"
                                        value-field="id"
                                        show-default-option
                                        default-option-text="Todas"
                                    />
                                </rkm-col>

                                <rkm-col>
                                    <rkm-form-select
                                        :options="form.classificacoesRisco"
                                        label="Classificação de risco"
                                        text-field="nome"
                                        v-model="model.classificacaoRiscoId"
                                        value-field="id"
                                        show-default-option
                                        default-option-text="Todas"
                                    />
                                </rkm-col>

                                <rkm-col>
                                    <rkm-form-select
                                        :options="form.status"
                                        @change="handleStatusChange"
                                        label="Status"
                                        text-field="nome"
                                        v-model="model.statusId"
                                        value-field="id"
                                    >
                                        <rkm-form-select-option
                                            v-if="form.status.length === 0"
                                            :value="null"
                                            slot="first"
                                        >
                                            Selecione
                                        </rkm-form-select-option>
                                    </rkm-form-select>
                                </rkm-col>
                            </rkm-row>
                        </rkm-area>

                        <rkm-area
                            v-if="form.salas.length > 0"
                            no-margin-top
                            title="Salas"
                            type="fieldset"
                        >
                            <rkm-row>
                                <rkm-col>
                                    <rkm-form-radio-group
                                        :options="form.salas"
                                        @change="handleSalaChange"
                                        button-color="outline-primary"
                                        buttons
                                        text-field="nome"
                                        v-model="model.salaId"
                                        value-field="id"
                                    />
                                </rkm-col>
                            </rkm-row>
                        </rkm-area>

                        <slot name="filters-bottom" />
                    </template>

                    <template v-slot:cell(tempoEspera)="row">
                        <rkm-icon
                            v-if="row.item.tempoEspera.id"
                            :id="uniqueId('tempo-espera-tooltip')"
                            :style="'color: ' + row.item.tempoEspera.cor"
                            :title="handleTempoEspera(row.item.tempoEspera.total)"
                            font-scale="2"
                            icon="battery-full"
                            v-rkm-tooltip.hover
                        />

                        <span v-else>
                            -
                        </span>

                        <slot
                            name="tempo-espera"
                            v-bind:item="row.item"
                        />
                    </template>

                    <template v-slot:cell(prioridade)="row">
                        {{ row.item.prioridade.nome }}

                        <rkm-icon
                            v-if="row.item.atendimento.recepcao.urgente"
                            :title="row.item.atendimento.recepcao.urgenteMotivo"
                            class="ml-2"
                            color="red"
                            icon="exclamation-triangle"
                            v-rkm-tooltip.hover
                        />
                    </template>

                    <template v-slot:cell(idade)="row">
                        <template v-if="row.item.atendimento.paciente.cidadao.hasIdade">
                            {{ row.item.atendimento.paciente.cidadao.idade.anos }} {{ row.item.atendimento.paciente.cidadao.idade.anos > 1 ? 'anos' : 'ano' }},
                            {{ row.item.atendimento.paciente.cidadao.idade.meses }} {{ row.item.atendimento.paciente.cidadao.idade.meses > 1 ? 'meses' : 'mês ' }} e
                            {{ row.item.atendimento.paciente.cidadao.idade.dias }} {{ row.item.atendimento.paciente.cidadao.idade.dias > 1 ? 'dias' : 'dia' }}
                        </template>

                        <template v-else>
                            Data de nascimento não preenchida
                        </template>

                    </template>

                    <template v-slot:cell(nome)="{ item }">
                        <rkm-button
                            :id="uniqueId(`paciente-icon-${item.id}`)"
                            class="mr-2"
                            small
                        >
                            <rkm-icon icon="user"/>
                        </rkm-button>

                        <rkm-popover
                            :target="uniqueId(`paciente-icon-${item.id}`)"
                            triggers="hover"
                            placement="top"
                            title="Dados cadastrais"
                        >
                            <div>
                                <span>
                                    <strong>Data de nascimento:</strong> {{ item.atendimento.paciente.cidadao.dataNascimento }}
                                </span>
                            </div>

                            <div>
                                <span>
                                    <strong>Nome da mãe:</strong> {{ item.atendimento.paciente.cidadao.nomeMae }}
                                </span>
                            </div>
                        </rkm-popover>

                        <span
                            :id="uniqueId(`paciente-nome-${item.id}`)"
                            :class="{ 'text-primary': item.atendimento.paciente.isDesconhecido }"
                        >
                            {{ item.atendimento.paciente.cidadao.nome }}

                            <template v-if="item.atendimento.paciente.cidadao.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                                <strong>
                                    ({{ item.atendimento.paciente.cidadao.nomeSocial }})
                                </strong>
                            </template>
                        </span>

                        <rkm-popover
                            v-if="item.atendimento.paciente.isDesconhecido"
                            :target="uniqueId(`paciente-nome-${item.id}`)"
                            triggers="hover"
                            placement="top"
                            title="Características físicas"
                        >
                            <span>
                                {{ item.atendimento.paciente.cidadao.caracteristicasFisicas }}
                            </span>
                        </rkm-popover>

                        <rkm-icon
                            v-if="setorTipoId === enums.setorTipo.CONSULTORIO && item.atendimento.hasAtendimentoMedico"
                            class="ml-2"
                            icon="undo-alt"
                        />
                    </template>

                    <template v-slot:cell(actions)="row">
                        <template v-if="form.telas.length > 0 && !row.item.atendimento.paciente.desconhecido">
                            <rkm-dropdown
                                v-if="form.telas.length > 1"
                                :color="row.item.chamadas > 0 ? 'secondary' : 'info'"
                                class="mr-2"
                                small
                                title="Chamar"
                                v-rkm-tooltip.hover
                            >
                                <template v-slot:button-content>
                                    <rkm-icon icon="volume-up" />

                                    <rkm-badge v-if="row.item.chamadas > 0">
                                        {{ row.item.chamadas }}
                                    </rkm-badge>
                                </template>

                                <rkm-dropdown-item
                                    v-for="tela in form.telas"
                                    v-bind:key="tela.id"
                                    @click="handleChamadaClick(row.item, tela)"
                                >
                                    {{ tela.nome }}
                                </rkm-dropdown-item>
                            </rkm-dropdown>

                            <rkm-button
                                v-else
                                :color="row.item.chamadas > 0 ? 'secondary' : 'info'"
                                @click="handleChamadaClick(row.item, form.telas[0])"
                                small
                                title="Chamar"
                            >
                                <rkm-icon icon="volume-up" />

                                <rkm-badge v-if="row.item.chamadas > 0">
                                    {{ row.item.chamadas }}
                                </rkm-badge>
                            </rkm-button>
                        </template>

                        <slot
                            name="custom-actions"
                            v-bind:item="row.item"
                        />
                    </template>
                </rkm-table>
            </rkm-col>
        </rkm-row>

        <rkm-row class="mt-2">
            <rkm-col>
                <p class="m-0">
                    Pacientes em espera: {{ model.fila.filter(item => item.status.id === enums.status.EM_ESPERA).length }}
                </p>

                <p class="m-0">
                    Pacientes em atendimento: {{ model.fila.filter(item => item.status.id === enums.status.EM_ATENDIMENTO).length }}
                </p>

                <p class="m-0">
                    Linhas em <span class="warning">amarelo</span> representam pacientes na fila por mais de 24 horas.
                </p>
            </rkm-col>
        </rkm-row>

        <ModalChamada
            :salas="form.salas"
            ref="ModalChamada"
        />
    </div>
</template>

<script>
// Components.
import ModalChamada from '@/components/pronto-atendimento/fila/components/chamada/Modal'

// Enums.
import modulo from '@/enums/administracao/modulo'
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import setorTipo from '@/enums/cadastro/estabelecimento/setorTipo'
import status from '@/enums/painel-senha/status'

// Mixins.
import { error, repository, stringCase, websocket } from '@/mixins'

// Vuex.
import { mapActions, mapGetters } from "vuex"

export default {
    components: { ModalChamada },
    computed: {
        ...mapGetters({
            centroCusto: 'getCentroCusto',
            customer: 'getCustomer',
            getFila: 'getFila',
            lotacao: 'getLotacao',
        }),
        filteredItems() {
            let filtered = this.model.fila

            if (this.model.tempoEsperaId) {
                filtered = filtered.filter(item => item.tempoEspera.id == this.model.tempoEsperaId)
            }

            if (this.model.opcaoEntradaId) {
                filtered = filtered.filter(item => item.opcaoEntrada.id == this.model.opcaoEntradaId)
            }

            if (this.model.prioridadeId) {
                filtered = filtered.filter(item => item.prioridade.id == this.model.prioridadeId)
            }

            if (this.model.classificacaoRiscoId) {
                filtered = filtered.filter(item => item.atendimento.classificacaoRisco.id == this.model.classificacaoRiscoId)
            }

            if (this.model.statusId) {
                filtered = filtered.filter(item => item.status.id == this.model.statusId)
            }

            if (this.plantaoFaixaEtariaId) {

                filtered = filtered.filter(item => item.opcaoEntrada.faixaEtariaId == this.plantaoFaixaEtariaId
                    || !item.opcaoEntrada.faixaEtariaId )
            }

            return filtered
        },
    },
    created() {
        this.repositories.chamada = this.getConectaSusRepository('prontoAtendimento.chamada')
        this.repositories.fila = this.getConectaSusRepository('prontoAtendimento.fila')
    },
    data: () => ({
        enums: {
            modulo,
            preferenciaNome,
            setorTipo,
            status,
        },
        form: {
            classificacoesRisco: [],
            opcoesEntrada: [],
            prioridades: [],
            salas: [],
            sortOptions: [
                { value: null, text: 'CLASSIFICAÇÃO DE RISCO / PRIORIDADE' },
                { value: 'createdAt', text: 'DATA / HORA DE CHEGADA' },
            ],
            status: [],
            temposEspera: [],
        },
        model: {
            classificacaoRiscoId: null,
            filter: null,
            fila: [],
            opcaoEntradaId: null,
            prioridadeId: null,
            sortBy: null,
            statusId: null,
            tempoEsperaId: null,
            salaId: null,
        },
        repositories: {},
        busy: false,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading', 'setFila']),
        init() {
            this.model.sortBy = this.getFila ? this.getFila.sortBy : null
            this.model.salaId = this.getFila ? this.getFila.salaId : null

            this.pusher.subscribe(`private-${this.customer.link}-painel-senha-fila`)
                .bind(`${this.setorTipoId}.${this.centroCusto.id}.${this.enums.modulo.PRONTO_ATENDIMENTO}.updated`, (data) => {
                    this.model.fila = this.snakeObjectKeysToCamelRecursive(data.fila)
                })
        },
        load() {
            this.setLoading()

            const request = {
                centro_custo_id: this.centroCusto.id,
                setor_tipo_id: this.setorTipoId,
            }

            this.repositories.fila.list(request)
                .then(response => {
                    this.form = Object.assign(this.form, response.data.form)
                    this.model = Object.assign(this.model, response.data.model)
                })
                .finally(() => this.unsetLoading())
        },
        handleChamadaClick(fila, tela) {
            const params = {
                fila_id: fila.id,
                tela_id: tela.id,
                sala_id: this.model.salaId,
            }

            if (this.form.salas.length > 0 && !this.model.salaId) {
                return this.$refs.ModalChamada.open(fila, tela, this.form.salas)
            }

            this.busy = true

            this.repositories.chamada.store(params)
                .finally(() => this.busy = false)
        },
        handleRowAttr(item, type) {
            if (type === 'row') {
                if (item.atendimento.classificacaoRisco.tipo.cor) {
                    return {
                        style: `
                            border-left: 10px solid ${item.atendimento.classificacaoRisco.tipo.cor};
                            background-color: ${item.atendimento.classificacaoRisco.tipo.cor}15;
                        `
                    }
                }
            }
        },
        handleSortChange(value) {
            this.setFila({ sortBy: value })
        },
        handleStatusChange(value) {
            const status = this.form.status.find(status => status.id === value)

            if (status) {
                this.$emit('status-changed', status.tipoId)
            }
        },
        handleTempoEspera(value) {
            let format = 'Esperando por'

            if (value.dias !== null) {
                format = `${format} ${value.dias} dia(s)`
            }

            if (value.horas !== null) {
                format = `${format} ${value.horas} hora(s)`
            }

            if (value.minutos !== null) {
                if (value.horas !== null) {
                    format = `${format} e ${value.minutos} minuto(s)`
                } else {
                    format = `${format} ${value.minutos} minuto(s)`
                }
            }

            return format
        },
        handleSalaChange(value) {
            this.setFila({salaId: value})
        },
    },
    mounted() {
        this.init()
        this.load()
    },
    mixins: [ error, repository, stringCase, websocket ],
    props: {
        customFilter: { type: Function, default: data => data },
        fields: { type: Array, required: true },
        hidePagination: { type: Boolean, default: true },
        plantaoFaixaEtariaId: { type: Number, default: null },
        setorTipoId: { type: [Number, String], required: true },
    },
    watch: {
        'model.fila'(value) {
            value.map(item => {
                if (item.destacado) {
                    item._rowVariant = 'warning'
                }

                return item
            })
        },
    },
}
</script>

<style scoped>
.warning {
    color: var(--warning);
}
</style>